import { useCallback, useEffect, useState } from "react";
import { Api } from "services/api/api.service";
import type { BackendAxiosError } from "services/api/backendError";
import type { StaffProfileWorkGroup } from "services/api/company/staff.service";
import { err, ok, type Result } from "utils/result";
import {
  resolveDeleteWorkerErrorKey,
  resolveStaffProfilePanelErrorMessage,
} from "./domains/employment/profilePanel.error.logic";

interface AlertPayload {
  icon?: string;
  title: string;
  message: string;
}

interface AlertAction {
  type: "SHOW_ALERT";
  payload: AlertPayload;
}

type AlertDispatch = (action: AlertAction) => void;
type TranslateFn = (key: string) => string;

interface UseProfilePanelActionsLogicParams {
  isOpen: boolean;
  companyId: number | null;
  workerId: number | null;
  workGroups: StaffProfileWorkGroup[] | undefined;
  editedTeamIds: string[];
  updatePersonalField: (field: "teamIds", value: string[]) => void;
  reloadProfile: () => void;
  onClose: () => void;
  onWorkerUpdated?: () => void;
  onWorkerDeleted?: (workerId: number) => void;
  translate: TranslateFn;
  alertDispatch: AlertDispatch;
}

interface UseProfilePanelActionsLogicResult {
  isTeamModalOpen: boolean;
  isUpdatingTeams: boolean;
  teamUpdateError: string;
  isDeleteModalOpen: boolean;
  isDeletingWorker: boolean;
  isDeletingFutureEmployment: boolean;
  handleOpenChangeTeamModal: () => void;
  handleCloseChangeTeamModal: () => void;
  handleConfirmChangeTeamModal: () => Promise<void>;
  handleDeleteFutureEmployment: (employmentId: number) => Promise<boolean>;
  handleOpenDeleteModal: () => void;
  handleCloseDeleteModal: () => void;
  handleConfirmDeleteWorker: () => Promise<void>;
}

type TeamModalState =
  | {
      type: "closed";
      isUpdatingTeams: false;
      teamUpdateError: string;
    }
  | {
      type: "open";
      isUpdatingTeams: boolean;
      teamUpdateError: string;
    };

type DeleteModalState =
  | {
      type: "closed";
      isDeletingWorker: false;
    }
  | {
      type: "open";
      isDeletingWorker: boolean;
    };

interface DeleteWorkerParams {
  companyId: number;
  workerId: number;
}

const deleteWorker = async (
  params: DeleteWorkerParams,
): Promise<Result<void, BackendAxiosError>> => {
  return Api()
    .company.staff.deleteWorker(params.companyId, params.workerId)
    .then(() => ok(undefined))
    .catch((error: BackendAxiosError) => err(error));
};

/**
 * Handles use profile panel actions logic.
 */
export const useProfilePanelActionsLogic = (
  params: UseProfilePanelActionsLogicParams,
): UseProfilePanelActionsLogicResult => {
  const [teamModalState, setTeamModalState] = useState<TeamModalState>({
    type: "closed",
    isUpdatingTeams: false,
    teamUpdateError: "",
  });

  const [deleteModalState, setDeleteModalState] = useState<DeleteModalState>({
    type: "closed",
    isDeletingWorker: false,
  });
  const [isDeletingFutureEmployment, setIsDeletingFutureEmployment] =
    useState(false);

  const isTeamModalOpen = teamModalState.type === "open";

  const isUpdatingTeams =
    teamModalState.type === "open" ? teamModalState.isUpdatingTeams : false;

  const teamUpdateError = teamModalState.teamUpdateError;

  const isDeleteModalOpen = deleteModalState.type === "open";

  const isDeletingWorker =
    deleteModalState.type === "open"
      ? deleteModalState.isDeletingWorker
      : false;

  useEffect(() => {
    if (!params.isOpen) {
      setTeamModalState({
        type: "closed",
        isUpdatingTeams: false,
        teamUpdateError: "",
      });
      setDeleteModalState({
        type: "closed",
        isDeletingWorker: false,
      });
    }
  }, [params.isOpen]);

  const handleOpenChangeTeamModal = useCallback((): void => {
    const teamIds = params.workGroups?.map(
      (workGroup: StaffProfileWorkGroup): string => String(workGroup.id),
    );

    params.updatePersonalField("teamIds", teamIds ?? []);
    setTeamModalState({
      type: "open",
      isUpdatingTeams: false,
      teamUpdateError: "",
    });
  }, [params.updatePersonalField, params.workGroups]);

  const handleCloseChangeTeamModal = useCallback((): void => {
    setTeamModalState((previousState: TeamModalState): TeamModalState => {
      if (previousState.type === "open" && previousState.isUpdatingTeams) {
        return previousState;
      }
      return {
        type: "closed",
        isUpdatingTeams: false,
        teamUpdateError: "",
      };
    });
  }, []);

  const handleConfirmChangeTeamModal = useCallback(async (): Promise<void> => {
    if (params.companyId == null || params.workerId == null) {
      setTeamModalState(
        (previousState: TeamModalState): TeamModalState => ({
          type: previousState.type,
          isUpdatingTeams: false,
          teamUpdateError: params.translate("Staff.ProfilePanelLoadError"),
        }),
      );
      return;
    }

    if (isUpdatingTeams) {
      return;
    }

    const workGroupIds = params.editedTeamIds
      .map((teamId: string): number => Number(teamId))
      .filter(
        (teamId: number): boolean =>
          Number.isFinite(teamId) && Number.isInteger(teamId) && teamId > 0,
      );

    setTeamModalState({
      type: "open",
      isUpdatingTeams: true,
      teamUpdateError: "",
    });

    try {
      await Api().company.staff.updateWorkerWorkgroups(
        params.companyId,
        params.workerId,
        {
          workGroupIds,
        },
      );

      setTeamModalState({
        type: "closed",
        isUpdatingTeams: false,
        teamUpdateError: "",
      });

      params.reloadProfile();
    } catch {
      setTeamModalState(
        (previousState: TeamModalState): TeamModalState => ({
          ...previousState,
          isUpdatingTeams: false,
          teamUpdateError: params.translate("Staff.ProfilePanelLoadError"),
        }),
      );
    }
  }, [
    params.companyId,
    params.editedTeamIds,
    params.reloadProfile,
    params.translate,
    params.workerId,
    isUpdatingTeams,
  ]);

  const handleOpenDeleteModal = useCallback((): void => {
    setDeleteModalState({
      type: "open",
      isDeletingWorker: false,
    });
  }, []);

  const handleDeleteFutureEmployment = async (
    employmentId: number,
  ): Promise<boolean> => {
    if (
      params.companyId == null ||
      params.workerId == null ||
      isDeletingFutureEmployment
    ) {
      return false;
    }

    setIsDeletingFutureEmployment(true);
    try {
      await Api().company.staff.deleteWorkerEmployment(
        params.companyId,
        params.workerId,
        employmentId,
      );
      params.reloadProfile();
      params.onWorkerUpdated?.();
      params.alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          title: params.translate("Alerts.Updated"),
          message: params.translate(
            "Staff.ProfilePanelDeleteFutureEmploymentSuccess",
          ),
        },
      });
      return true;
    } catch (error) {
      params.alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: params.translate("Alerts.OhNo"),
          message: resolveStaffProfilePanelErrorMessage(
            error as BackendAxiosError,
            params.translate,
          ),
        },
      });
      return false;
    } finally {
      setIsDeletingFutureEmployment(false);
    }
  };

  const handleCloseDeleteModal = useCallback((): void => {
    setDeleteModalState((previousState: DeleteModalState): DeleteModalState => {
      if (previousState.type === "open" && previousState.isDeletingWorker) {
        return previousState;
      }
      return {
        type: "closed",
        isDeletingWorker: false,
      };
    });
  }, []);

  const handleConfirmDeleteWorker = useCallback(async (): Promise<void> => {
    if (params.companyId == null || params.workerId == null) {
      params.alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: params.translate("Alerts.OhNo"),
          message: params.translate("Staff.ProfilePanelLoadError"),
        },
      });
      return;
    }

    if (isDeletingWorker) {
      return;
    }

    setDeleteModalState({
      type: "open",
      isDeletingWorker: true,
    });

    const deleteWorkerResult = await deleteWorker({
      companyId: params.companyId,
      workerId: params.workerId,
    });
    if (deleteWorkerResult.ok) {
      setDeleteModalState({
        type: "closed",
        isDeletingWorker: false,
      });

      params.onClose();
      params.onWorkerDeleted?.(params.workerId);

      params.alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          title: params.translate("Alerts.Updated"),
          message: params.translate("Staff.ProfilePanelDeleteSuccess"),
        },
      });
      return;
    }

    setDeleteModalState({
      type: "closed",
      isDeletingWorker: false,
    });

    params.alertDispatch({
      type: "SHOW_ALERT",
      payload: {
        icon: "alert",
        title: params.translate("Alerts.OhNo"),
        message: params.translate(
          resolveDeleteWorkerErrorKey(deleteWorkerResult.error),
        ),
      },
    });
  }, [
    params.alertDispatch,
    params.companyId,
    params.onClose,
    params.onWorkerDeleted,
    params.translate,
    params.workerId,
    isDeletingWorker,
  ]);

  return {
    isTeamModalOpen,
    isUpdatingTeams,
    teamUpdateError,
    isDeleteModalOpen,
    isDeletingWorker,
    isDeletingFutureEmployment,
    handleOpenChangeTeamModal,
    handleCloseChangeTeamModal,
    handleConfirmChangeTeamModal,
    handleDeleteFutureEmployment,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleConfirmDeleteWorker,
  };
};
